import * as Sentry from '@sentry/react'

import { ProductCard } from '@lounge-fe/ui-kit'
import { Price } from './Price'
import { StandardProductCardQuickAddProps } from './types'
import { useAddCartItems } from '@/features/cart/hooks'
import { base64 } from '@/utils/base64'
import { AddCartItemPayload } from '@/features/cart/types'
import { useDataTranslation } from '@/features/localisation/hooks/useDataTranslation/useDataTranslation'
import { useTranslatedValue } from '@/pages/Product/hooks/useTranslatedValue'
import { useMemo } from 'react'
import type {
  QuickAddLinkedBottoms,
  QuickAddOption,
} from '@lounge-fe/ui-kit/src/patterns/ProductCard/QuickAdd/types'
import { mapProductOptionLabelOverride } from '../../utils'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

const getPayload = (
  id: number,
  color: string | undefined,
  setID: string | undefined,
  parentId?: number,
) => {
  const payload: AddCartItemPayload = {
    id,
    quantity: 1,
    properties: { color },
  }

  if (parentId) {
    payload.parent_id = parentId
  }

  if (setID) {
    payload.properties!._setID = setID
  }

  return payload
}

export const QuickAdd = Sentry.withErrorBoundary(
  ({
    exclusive,
    handle,
    image,
    title,
    price,
    compareAtPrice,
    color,
    options,
    inStock,
    variants,
    linkedBottoms,
    onQuickAdd,
    isLoading,
    onSuccess,
  }: StandardProductCardQuickAddProps) => {
    const { getFlag } = useFeatureFlags()
    const addItems = useAddCartItems({
      onSuccess,
      useRecentlyAdded: !onSuccess,
    })
    const { t } = useDataTranslation()

    const { translateValue } = useTranslatedValue()

    const translatedOptions = useMemo(
      () =>
        options.map((option): QuickAddOption => {
          const label = translateValue({
            preferredValue: option.label,
            fallbackValue: option.name,
            loadingState: undefined,
          })

          return {
            ...option,
            label: typeof label === 'string' ? label : undefined,
          }
        }),
      [options, translateValue],
    )

    const translatedLinkedBottoms = useMemo(
      () =>
        linkedBottoms.map(
          (linkedBottom): QuickAddLinkedBottoms => ({
            ...linkedBottom,
            options: linkedBottom.options.map((option): QuickAddOption => {
              const fallbackValue = t('product.choose_your_bottoms', {
                defaultValue: option.name,
              })

              const label = translateValue({
                preferredValue: option.label,
                fallbackValue,
                loadingState: undefined,
              })

              return {
                ...option,
                label: typeof label === 'string' ? label : undefined,
              }
            }),
          }),
        ),
      [linkedBottoms, translateValue],
    )

    if (!inStock) {
      return null
    }

    const handleSubmit = async (
      variantId: number,
      linkedBottomsVariantId?: number,
    ) => {
      let payload = [getPayload(variantId, color, undefined)]
      if (linkedBottomsVariantId) {
        const setID = base64.encode([variantId, linkedBottomsVariantId])
        payload = [
          getPayload(variantId, color, setID),
          getPayload(
            linkedBottomsVariantId,
            color,
            setID,
            getFlag('enable_nested_line_items') ? variantId : undefined,
          ),
        ]
      }
      await addItems.mutateAsync(payload)
      onQuickAdd?.()
    }

    return (
      <ProductCard.QuickAdd
        exclusive={exclusive}
        productTitle={title}
        productImage={image}
        price={<Price price={price} />}
        compareAtPrice={
          compareAtPrice ? <Price price={compareAtPrice} /> : undefined
        }
        color={color}
        options={translatedOptions}
        linkedBottoms={translatedLinkedBottoms}
        variants={variants}
        onSubmit={handleSubmit}
        onUnhandledException={Sentry.captureException}
        isLoading={isLoading}
      />
    )
  },
  {},
)
